import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useKaraoke } from '../useKaraoke.js';

/**
 * Cuerpo del mensaje del coach con dos capas:
 *   1. Reveal + karaoke real (datos del WS vía `useKaraoke`).
 *   2. Selección tappable de palabra/frase (Bloque C) — banda continua.
 *
 * `tappable` se activa solo cuando el mensaje terminó de revelarse (no se puede
 * seleccionar texto que todavía está apareciendo).
 */
function tokenizeWords(text: string): ReadonlyArray<{ text: string; ws: boolean; start: number }> {
  const tokens: { text: string; ws: boolean; start: number }[] = [];
  let cursor = 0;
  for (const part of text.split(/(\s+)/)) {
    if (part === '') continue;
    tokens.push({ text: part, ws: /^\s+$/.test(part), start: cursor });
    cursor += part.length;
  }
  return tokens;
}

/**
 * The translate-it challenge as a block inside the text (plan 47 Round 2),
 * in coordinates of `text` (see `locateChallengeBlock`): `[start, end)` is the
 * sentence the block shows, `[spanStart, start)` and `[end, spanEnd)` the
 * markers around it (quotes), never shown. The words keep their offsets and
 * word indexes, so the karaoke and tap-a-word work on them as anywhere else.
 */
export interface SpokenChallengeBlock {
  spanStart: number;
  start: number;
  end: number;
  spanEnd: number;
  /** The block's head row («Ronda N», «No sé»). */
  head: ReactNode;
  ariaLabel: string;
  /** The game's colour (`--dir-color`). */
  style?: CSSProperties | undefined;
}

type Segment = 'before' | 'hidden' | 'block' | 'after';

function segmentOf(block: SpokenChallengeBlock | null, at: number): Segment {
  if (!block || at < block.spanStart) return 'before';
  if (at < block.start) return 'hidden';
  if (at < block.end) return 'block';
  if (at < block.spanEnd) return 'hidden';
  return 'after';
}

/** The cut points of `[start, end)` at the block's edges. */
function piecesOf(
  block: SpokenChallengeBlock | null,
  start: number,
  end: number,
): ReadonlyArray<readonly [number, number]> {
  if (!block) return [[start, end]];
  const cuts = [start];
  for (const c of [block.spanStart, block.start, block.end, block.spanEnd]) {
    if (c > start && c < end) cuts.push(c);
  }
  cuts.push(end);
  const out: Array<readonly [number, number]> = [];
  for (let i = 0; i + 1 < cuts.length; i += 1) {
    if (cuts[i + 1]! > cuts[i]!) out.push([cuts[i]!, cuts[i + 1]!]);
  }
  return out;
}

interface SpokenBodyProps {
  text: string;
  /**
   * Rangos `[start, end)` (en coordenadas de `text`, que ya viene limpio) a
   * renderizar en negrita. La negrita es una capa de formato encima del texto
   * limpio — los marcadores `**` no están en `text`, así el karaoke (que indexa
   * por carácter contra el alignment del TTS) no se desincroniza. Ver
   * `@orborix/shared-types` markdown-speech.ts.
   */
  boldRanges?: ReadonlyArray<readonly [number, number]>;
  /** Burbuja VIVA: revelado progresivo + caret + animación de entrada. */
  streaming: boolean;
  /**
   * Burbuja CERRADA que está sonando de nuevo (botón de escuchar). El texto ya
   * está entero — sólo corre el resaltado, contra el reloj del `<audio>` del
   * replay. Sin esto el karaoke marcaba únicamente la primera vez que el coach
   * hablaba.
   */
  replaying?: boolean;
  getRenderEndIdx: () => number;
  getCharIndex: () => number;
  /** Palabras clickeables (selección de vocabulario). */
  tappable?: boolean;
  /** Rango seleccionado [start, end] en índices de PALABRA (inclusivo). */
  selRange?: readonly [number, number] | null;
  onWordTap?: (text: string, el: HTMLElement, wordIdx: number) => void;
  /** The translate-it challenge, drawn as a block inside the text (Round 2). */
  challengeBlock?: SpokenChallengeBlock | null;
}

export function SpokenBody({
  text,
  boldRanges = [],
  streaming,
  replaying = false,
  getRenderEndIdx,
  getCharIndex,
  tappable = false,
  selRange = null,
  onWordTap,
  challengeBlock = null,
}: SpokenBodyProps) {
  const { renderEnd, speakingChar } = useKaraoke(
    streaming || replaying,
    text,
    getRenderEndIdx,
    getCharIndex,
  );
  const tokens = useMemo(() => tokenizeWords(text), [text]);
  // Una palabra va en negrita si su rango [start, end) intersecta algún rango.
  const isBold = (start: number, end: number): boolean =>
    boldRanges.some(([bs, be]) => start < be && end > bs);
  const revealEnd = Number.isFinite(renderEnd) ? renderEnd : text.length;
  const karaokeActive = speakingChar >= 0;
  const stillStreaming = streaming && revealEnd < text.length;
  const showTypingDots = streaming && revealEnd <= 0;

  const inRange = (idx: number): boolean => !!selRange && idx >= selRange[0] && idx <= selRange[1];
  const isMulti = !!selRange && selRange[1] > selRange[0];

  if (showTypingDots) {
    return (
      <span className="spoken-body">
        <span className="dots-anim" aria-label="Generando respuesta" />
      </span>
    );
  }

  // Each token is cut at the block's edges (a quote glued to a word: «Ella)
  // and every piece goes to its segment; the pieces of one word share its
  // word index and its karaoke range, so it lights and taps as one word.
  const out: Record<Segment, ReactNode[]> = { before: [], hidden: [], block: [], after: [] };
  let wIdx = -1;
  tokens.forEach((t, i) => {
    const end = t.start + t.text.length;
    if (t.start >= revealEnd) return;
    if (!t.ws) wIdx += 1;
    const myIdx = wIdx;
    for (const [ps, pe] of piecesOf(challengeBlock, t.start, end)) {
      const seg = segmentOf(challengeBlock, ps);
      if (seg === 'hidden' || ps >= revealEnd) continue;
      const visible = t.text.slice(ps - t.start, Math.min(pe, revealEnd) - t.start);
      const key = ps === t.start ? `${i}` : `${i}:${ps}`;
      out[seg].push(renderToken(t, end, myIdx, visible, key, seg === 'block'));
    }
  });
  const blockShown = challengeBlock !== null && revealEnd > challengeBlock.start;

  return (
    <span className="spoken-body">
      {out.before}
      {blockShown && (
        <span
          className={`game-cblock${streaming ? ' is-live' : ''}`}
          role="group"
          aria-label={challengeBlock.ariaLabel}
          style={challengeBlock.style}
        >
          {challengeBlock.head}
          <span className="game-cblock__text" lang="es">
            {out.block}
          </span>
        </span>
      )}
      {out.after}
      {stillStreaming && <span className="stream-caret" aria-hidden="true" />}
    </span>
  );

  function renderToken(
    t: { text: string; ws: boolean; start: number },
    end: number,
    myIdx: number,
    visible: string,
    key: string,
    inBlock: boolean,
  ): ReactNode {
    if (t.ws) {
      // Whitespace entre la palabra myIdx y myIdx+1: pintarlo si ambas están
      // en el rango → la frase se lee como una banda continua.
      if (inRange(myIdx) && inRange(myIdx + 1)) {
        return (
          <span key={key} className="spoken-tok is-selected is-sel-gap">
            {visible}
          </span>
        );
      }
      return <span key={key}>{visible}</span>;
    }
    // Inside the challenge block the block is the emphasis: no bold on top.
    const bolded = !inBlock && isBold(t.start, end);
    const isSpeaking = karaokeActive && speakingChar >= t.start && speakingChar < end;
    const isUnspoken = karaokeActive && t.start > speakingChar;
    const selected = inRange(myIdx);
    // `reveal` (fade+blur de entrada) SÓLO mientras streamea: ahí cada
    // palabra monta al revelarse y la animación acompaña. En una burbuja
    // cerrada todos los tokens montan JUNTOS — replayar tokReveal en bloque
    // hacía flashear el texto completo (opacity 0 → 1) justo al commitear
    // la burbuja viva al historial.
    const cls = streaming ? ['spoken-tok', 'reveal'] : ['spoken-tok'];
    if (isSpeaking) cls.push('is-speaking');
    else if (isUnspoken) cls.push('is-unspoken');
    if (tappable) cls.push('is-tappable');
    if (selected) {
      cls.push('is-selected');
      if (!isMulti || !selRange) cls.push('is-sel-solo');
      else if (myIdx === selRange[0]) cls.push('is-sel-start');
      else if (myIdx === selRange[1]) cls.push('is-sel-end');
      else cls.push('is-sel-mid');
    }
    const handleClick =
      tappable && onWordTap
        ? (e: React.MouseEvent<HTMLSpanElement>) => {
            e.stopPropagation();
            onWordTap(t.text, e.currentTarget, myIdx);
          }
        : undefined;
    return (
      <span key={key} className={cls.join(' ')} data-widx={myIdx} onClick={handleClick}>
        {bolded ? <strong>{visible}</strong> : visible}
      </span>
    );
  }
}
